import { Component, OnInit, ChangeDetectionStrategy, input, output, signal, effect, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChatLoggingService } from '../../services/chat-logging.service';

export class HeaderButton {
  id: string;
  tooltip?: string;
  icon?: string;
  fileSrc?: string;
  disabled?: boolean;
  customColors?: boolean;
}

@Component({
  selector: 'mi-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
  // Input signals
  fontSize = input<number>(24);
  headerHeight = input<number>(63);
  showBackButton = input<boolean>(false);
  showSearch = input<boolean>(false);
  search = input<string>('');
  title = input<string>('');
  buttons = input<HeaderButton[]>([]);
  buttonTooltipPlacement = input<string[]>();

  // Internal state signals
  searchFocused = signal(false);
  searchTerm$ = new Subject<string>();
  private _searchModel = signal('');

  // Getter/setter for ngModel binding
  get searchModel(): string {
    return this._searchModel();
  }
  set searchModel(value: string) {
    this._searchModel.set(value);
  }

  // Signal-based outputs
  searchChange = output<string>();
  onButtonClick = output<string>();
  onSearch = output<string>();

  private chatLoggingService = inject(ChatLoggingService);

  constructor() {
    // Effect to setup search when showSearch becomes true
    effect(() => {
      if (this.showSearch()) {
        this.setupSearch();
      }
    });

    // Effect to emit search changes
    effect(() => {
      const searchValue = this.search();
      this.searchChange.emit(searchValue);
    });

    // Effect to sync search input with searchModel
    effect(() => {
      const searchValue = this.search();
      this._searchModel.set(searchValue);
    });
  }

  ngOnInit() {}

  private setupSearch(): void {
    this.searchTerm$.pipe(debounceTime(1000)).subscribe(
      search => {
        try {
          if (search.trim() == '') {
            this.onSearch.emit('');
          } else {
            this.onSearch.emit(search);
          }
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'header.component.setupSearch(): Error in subscribe',
            logParameters: [ex]
          });
        }
      },
      ex => {
        this.chatLoggingService.error({
          logMessage: 'header.component.setupSearch(): Error in searchTerm',
          logParameters: [ex]
        });
      }
    );
  }

  public buttonClick(key: string) {
    this.onButtonClick.emit(key);
  }

  public searchClick() {
    const searchValue = this.searchModel;
    if (searchValue && searchValue.trim()) {
      this.onSearch.emit(searchValue);
    }
  }
}
